import { Check, Phone } from "lucide-react";
import { SITE } from "@/constants/site";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const TIERS = [
  {
    name: "Single Item",
    price: "115",
    items: ["Couches & mattresses", "Washers, dryers & TVs", "Curbside or in-home pickup"],
    featured: false,
  },
  {
    name: "1/4 Truckload",
    price: "215",
    items: ["A few furniture pieces", "Small garage or shed piles", "Bagged yard debris & branches"],
    featured: false,
  },
  {
    name: "Half Truckload",
    price: "425",
    items: ["Garage & basement cleanouts", "Yard debris & renovation waste", "Most popular for moving prep"],
    featured: true,
  },
  {
    name: "3/4 Truckload",
    price: "650",
    items: ["Multi-room cleanouts", "Large renovation debris", "Office furniture & equipment"],
    featured: false,
  },
  {
    name: "Full Truckload",
    price: "850",
    items: ["Estate & whole-home cleanouts", "Office & commercial jobs", "Light demolition included"],
    featured: false,
  },
];

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
    <Reveal>
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">Pricing</p>
      <h2 className="mb-6 font-display text-4xl font-black italic uppercase leading-none tracking-tighter sm:text-5xl lg:text-[5vw]">
        Straight pricing. <span className="text-acid">No games.</span>
      </h2>
      <p className="mb-16 max-w-xl text-base leading-relaxed text-zinc-400">
        Every job gets a free, no-obligation quote before we lift a thing.
        You approve the price first — it never changes after that.
      </p>
    </Reveal>

    <div className="grid gap-6 md:grid-cols-3 md:gap-8">
      {TIERS.map((tier, i) => (
        <Reveal key={tier.name} delay={i * 0.1}>
          <div
            data-testid={`pricing-tier-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`flex h-full flex-col border p-8 transition-transform duration-300 hover:-translate-y-1 ${
              tier.featured ? "border-acid bg-coal" : "border-seam bg-ink"
            }`}
          >
            {tier.featured && (
              <span className="mb-4 w-fit bg-acid px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-black">
                Most popular
              </span>
            )}
            <h3 className="font-display text-2xl font-extrabold italic uppercase tracking-tight">{tier.name}</h3>
            <div className="mt-6 flex items-end gap-2">
              <span className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">from</span>
              <span className={`font-display text-7xl font-black italic leading-none tracking-tighter ${tier.featured ? "text-acid" : "text-white"}`}>
                ${tier.price}
              </span>
            </div>
            <ul className="mt-8 flex flex-col gap-3">
              {tier.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-acid" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              data-testid={`pricing-quote-btn-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => scrollToId("quote")}
              className={`mt-10 w-full py-3.5 font-display text-lg font-extrabold italic uppercase tracking-tight transition-colors duration-200 ${
                tier.featured
                  ? "bg-acid text-black hover:bg-white"
                  : "border border-seam text-white hover:border-acid hover:text-acid"
              }`}
            >
              Get this quote
            </button>
          </div>
        </Reveal>
      ))}
    </div>

    <Reveal delay={0.2}>
      <p className="mt-10 flex flex-wrap items-center gap-2 text-sm text-zinc-500">
        Not sure which fits? One call and we’ll size it up:
        <a data-testid="pricing-call-link" href={`tel:${SITE.phoneTel}`} className="flex items-center gap-1 font-bold text-acid hover:underline">
          <Phone className="h-3.5 w-3.5" /> {SITE.phoneDisplay}
        </a>
      </p>
    </Reveal>
  </section>
);
